import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useCallback, useMemo } from "react";
import {
  deleteOnboardUser,
  disableAddModal,
  enableAddModal,
  selectBmrAddModal,
} from "../../bmr/slices/onBoardUser";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  EditIconButton,
  EyeIconButton,
} from "../../assets/common-svg/CommonSvg";
import BsModal from "../../bmr/components/common/BaseModal";
import Datatable from "../../bmr/components/features/Datatable";
import DeleteModal from "../../bmr/components/common/BaseModal/DeleteModal";
import ListDrawer from "../../components/ListDrawer/ListDrawer";
import AssignNewPermission from "./assign-new-permission";
import { useNavigate } from "react-router";
import AssignedFormFilters from "./form-assigned-filter";
import { CustomSwitch } from "../../components/custom-switch/CustomSwitch";
import {
  fetchAllAssignedTemplates,
  updatePermission,
} from "./slice/FbAssignedPermission";
import { templateDropdown } from "../../bmr/slices/formTemplate";
import { combinedRoles } from "../../bmr/slices/roles";
import { fetchProductTypes } from "./slice/FbAssignedPermission";
import EditorApi from "../../services/editorManagementService";
import { notify } from "../../bmr/utils/methods/notification";

const PAGINATION_KEYS = ["page", "per_page", "sort_by", "sort_order", "search"];

const FILTER_KEYS = [
  "product_id",
  "phase_id",
  "status",
  "version",
  "template_id",
];

const buildApiParams = (paginationParams = {}, activeFilters = {}) => {
  const params = {};
  PAGINATION_KEYS.forEach((key) => {
    const val = paginationParams[key];
    if (val !== undefined && val !== null && val !== "") {
      params[key] = val;
    }
  });

  FILTER_KEYS.forEach((key) => {
    const val = activeFilters[key];
    if (val !== undefined && val !== null && val !== "") {
      params[key] = val;
    }
  });

  return params;
};

const FormAssigned = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [openDrawer, setOpenDrawer] = useState(false);
  const [allFilters, setFilters] = useState({});
  const { AssignedTemplatesList } = useSelector(
    (state) => state?.assignedTemplates,
  );
  const LoginData = useSelector((state) => state.loginData);

  const addModal = useSelector(selectBmrAddModal);
  const [rowId, setRowId] = useState(null);
  const [open, setOpen] = useState(false);
  const [viewPermissions, setViewPermissions] = useState({});
  const [fetchData, setFetchData] = useState({});
  const [productData, setProductData] = useState([]);
  const [phaseData, setPhaseData] = useState([]);

  const fetchProductAndPhase = useCallback(async () => {
    try {
      const { role_slug, loginId } = LoginData;
      const res = await EditorApi.getPhaseDropDown(
        "",
        role_slug === "investigator" ? loginId : undefined,
        role_slug === "editor" ? loginId : undefined,
        role_slug === "project-manager" ? loginId : undefined,
      );

      setPhaseData(
        res.data.map((item) => ({
          value: item.id,
          label: item.title,
          productId: item.product_details?.id,
        })),
      );

      setProductData([
        ...new Map(
          res.data
            .filter((item) => item.product_details)
            .map((item) => [
              item.product_details.id,
              {
                value: item.product_details.id,
                label: item.product_details.name,
              },
            ]),
        ).values(),
      ]);
    } catch (error) {
      notify.error(error);
    }
  }, [LoginData]);

  useEffect(() => {
    // dispatch(fetchAllRoles());
    dispatch(combinedRoles());
    dispatch(fetchProductTypes());
    dispatch(templateDropdown());
    fetchProductAndPhase();
  }, [dispatch, fetchProductAndPhase]);

  useEffect(() => {
    if (AssignedTemplatesList?.items?.length > 0) {
      const initial = {};
      AssignedTemplatesList.items.forEach((item) => {
        initial[item.assign_form_id] = item.status === 1;
      });
      setViewPermissions(initial);
    }
  }, [AssignedTemplatesList]);

  const updateFilter = (filter) => {
    const paginationParams = Object.fromEntries(
      PAGINATION_KEYS.map((k) => [k, filter?.[k]]).filter(
        ([, v]) => v !== undefined,
      ),
    );
    const incomingFilterKeys = FILTER_KEYS.filter(
      (k) => filter?.[k] !== undefined && filter?.[k] !== "",
    );
    const isReset =
      incomingFilterKeys.length === 0 &&
      !filter?.product_id &&
      !filter?.phase_id &&
      !filter?.status &&
      !filter?.version &&
      !filter?.template_id;

    const newFilters = isReset
      ? {}
      : {
          ...allFilters,
          ...Object.fromEntries(
            FILTER_KEYS.map((k) => [k, filter?.[k] ?? allFilters[k]]),
          ),
        };

    FILTER_KEYS.forEach((k) => {
      if (!newFilters[k] && newFilters[k] !== 0) delete newFilters[k];
    });

    setFilters(newFilters);
    dispatch(
      fetchAllAssignedTemplates(buildApiParams(paginationParams, newFilters)),
    );
  };

  const FilterWithData = useMemo(
    () =>
      function FilterWithDataInner(props) {
        return (
          <AssignedFormFilters
            {...props}
            productData={productData}
            phaseData={phaseData}
          />
        );
      },
    [productData, phaseData],
  );

  const handleCloseModal = async () => {
    setRowId(null);
    await dispatch(disableAddModal());
  };

  const handleAddModal = async () => {
    await dispatch(enableAddModal());
  };

  const handleDelete = async () => {
    await dispatch(deleteOnboardUser({ editId: rowId, filters: allFilters }));
    setOpen(false);
  };

  const handleViewToggle = (e, id) => {
    const checked = e.target.checked;
    setViewPermissions((prev) => ({ ...prev, [id]: checked }));
    dispatch(updatePermission({ id, status: checked ? 1 : 0 }));
  };

  const columns = [
    {
      id: "template_name",
      name: "Template Name",
      selector: (row) => row.template_name,
      sortable: true,
    },
    {
      id: "version",
      name: "Version",
      selector: (row) => <> {`V.${row.version}`}</>,
      sortable: true,
    },
    {
      id: "phase_type",
      name: "Phase",
      selector: (row) => (
        <>
          <Typography sx={{ color: "#000", textTransform: "capitalize" }}>
            {row?.phase_title}
          </Typography>

          <Typography
            variant="body1"
            sx={{ color: "#6B7280", textTransform: "capitalize" }}
          >
            {row?.product_name}
          </Typography>
        </>
      ),
      sortable: true,
    },
    {
      id: "product_fb_type_type",
      name: "Form Type",
      selector: (row) => row.product_fb_type_type,
      sortable: true,
    },
    {
      id: "approval_level",
      name: "Approval Level",
      selector: (row) => row.approval_level,
      sortable: true,
    },
    {
      id: "roles_for_approval",
      name: "Roles for Approval",
      selector: (row) => row.roles_for_approval,
      sortable: true,
      align: "center",
    },
    {
      id: "active_inactive",
      name: "Active / In-Active",
      selector: (row) => (
        <Box key={row.assign_form_id}>
          <CustomSwitch
            toggle={viewPermissions[row.assign_form_id] || false}
            handleSwitch={(e) => handleViewToggle(e, row.assign_form_id)}
            disableChip={false}
          />
        </Box>
      ),
      sortable: true,
      align: "center",
    },
    {
      name: "Action",
      align: "center",
      selector: (row) => (
        <Box key={row.id}>
          <Tooltip title="View Permission">
            <span>
              <IconButton
                onClick={() =>
                  navigate(
                    `/bmr/assigned-permission/view/${row?.template_id}/${row?.assign_form_id}/assign-section-permission`,
                    {
                      state: [
                        {
                          name: "Assign Template",
                          path: "/bmr/assigned-permission",
                        },
                        {
                          name: "Assign Section Permission",
                          path: `/bmr/assigned-permission/view/${row?.template_id}/${row?.assign_form_id}/assign-section-permission`,
                        },
                      ],
                    },
                  )
                }
              >
                <EyeIconButton bgColor={"#3571FF"} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Edit">
            <span>
              <IconButton
                onClick={() => {
                  setRowId(row.assign_form_id);
                  handleAddModal();
                  setFetchData(row);
                }}
              >
                <EditIconButton bgColor={"#C3C3C3"} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Grid
      container
      className="m-4 ml-0 mainDiv"
      rowSpacing={4.5}
      columnSpacing={2.75}
    >
      <BsModal
        open={addModal}
        size={"sm"}
        onClose={handleCloseModal}
        title={rowId ? "Edit Assign Templates" : "Assign Template Permission"}
      >
        <AssignNewPermission
          editId={rowId}
          filters={allFilters}
          fetchData={fetchData}
          productData={productData}
          phaseData={phaseData}
        />
      </BsModal>

      <Grid item xs={12} sx={{ mt: -2.25 }}>
        <Datatable
          columns={columns}
          data={{
            items: AssignedTemplatesList?.items,
            total_count: AssignedTemplatesList?.pagination?.total,
          }}
          title="Assigned Templates Permission"
          searchPlaceholder="Search by name, template, product…"
          BtnComponent={
            <Button
              className="ml-2 w-auto"
              onClick={() => {
                handleAddModal();
                setRowId(null);
              }}
            >
              Assign Template Permission
            </Button>
          }
          FilterComponent={FilterWithData}
          remote={true}
          filterConfig={{
            product_id: { label: "Product" },
            phase_id: { label: "Phase" },
            status: { label: "Status" },
            version: { label: "Version" },
          }}
          onServerSideChange={updateFilter}
        />
      </Grid>

      <DeleteModal
        open={open}
        setClose={setOpen}
        title={"Are you sure you want to Delete?"}
        buttonText={"Delete"}
        handleSuccess={handleDelete}
      />

      {openDrawer && (
        <ListDrawer
          open={openDrawer}
          toggleDrawer={() => setOpenDrawer(!openDrawer)}
          listData={[]}
          title="Phase List"
        />
      )}
    </Grid>
  );
};

export default FormAssigned;
